import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { adminApi, authApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { useAuthStore } from '../../store/auth';

import { Modal } from '../../components/Modal';

const LOCKED_CODES = ['SUPER_ADMIN', 'SYSTEM_AUDITOR'];

/**
 * Roles & Permissions — FR-ADMIN-02
 * Two-pane: role list left, detail + permission editor right.
 * System roles have metadata edit but permissions are fixed.
 * SUPER_ADMIN and SYSTEM_AUDITOR are fully locked.
 */
export function RolesPage() {
  const qc = useQueryClient();
  const setStorePermissions = useAuthStore((s) => s.setPermissions);
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showDeleteRole, setShowDeleteRole] = useState(false);

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: adminApi.listRoles });
  const allPermsQuery = useQuery({ queryKey: ['permissions'], queryFn: adminApi.listPermissions });
  const rolePermsQuery = useQuery({
    queryKey: ['role-perms', selected],
    queryFn: () => adminApi.getRolePermissions(selected!),
    enabled: !!selected,
  });

  const selectedRole = useMemo(
    () => rolesQuery.data?.find((r: any) => r.id === selected),
    [rolesQuery.data, selected]
  );

  const syncMyPermissions = async () => {
    try {
      const { roles, permissions } = await authApi.permissions();
      setStorePermissions(roles, permissions);
    } catch {
      // non-fatal
    }
  };

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteRole(id),
    onSuccess: () => {
      toast.success('Role deleted');
      qc.invalidateQueries({ queryKey: ['roles'] });
      setSelected(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = useMemo(() => {
    const rows = rolesQuery.data ?? [];
    return {
      total: rows.length,
      system: rows.filter((r: any) => r.is_system).length,
      custom: rows.filter((r: any) => !r.is_system).length,
      totalUsers: rows.reduce((a: number, r: any) => a + (r.user_count || 0), 0),
    };
  }, [rolesQuery.data]);

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Administration"
        title="Roles & permissions"
        subtitle="RBAC — who can do what. System roles are fixed; create custom roles for your team."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon /> New role
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total roles" value={kpis.total} />
        <KpiTile label="System roles" value={kpis.system} />
        <KpiTile label="Custom roles" value={kpis.custom} accent="sage" />
        <KpiTile label="Users assigned" value={kpis.totalUsers} />
      </div>

      {/* Two-pane */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_36rem] gap-6">
        <div className="card overflow-hidden">
          {rolesQuery.isLoading ? (
            <LoadingState rows={5} />
          ) : rolesQuery.error ? (
            <ErrorState error={rolesQuery.error} />
          ) : !rolesQuery.data?.length ? (
            <EmptyState message="No roles yet" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Type</th>
                    <th className="text-right">Perms</th>
                    <th className="text-right">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {(rolesQuery.data ?? []).map((r: any) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelected(r.id)}
                      className={clsx('cursor-pointer', selected === r.id && '!bg-canvas-subtle')}
                    >
                      <td>
                        <div className="font-mono text-xs font-semibold text-ink-900">{r.code}</div>
                        <div className="font-semibold text-ink-900 mt-0.5">{r.name}</div>
                        {r.description && (
                          <div className="text-xs text-ink-500 mt-0.5 max-w-sm truncate">{r.description}</div>
                        )}
                      </td>
                      <td>
                        <span className={clsx(
                          'badge ring-1 ring-inset',
                          r.is_system
                            ? 'bg-canvas-subtle text-ink-700 ring-ink-200'
                            : 'bg-sage-50 text-sage-700 ring-sage-200'
                        )}>
                          {r.is_system ? 'System' : 'Custom'}
                        </span>
                      </td>
                      <td className="text-right font-mono text-sm text-ink-700 tabular-nums">
                        {r.permission_count}
                      </td>
                      <td className="text-right font-mono text-sm text-ink-700 tabular-nums">
                        {r.user_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select a role"
              hint="Click a row on the left to view or edit permissions."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              }
            />
          ) : selectedRole ? (
            <RoleDetail
              role={selectedRole}
              rolePermsQuery={rolePermsQuery}
              allPerms={allPermsQuery.data ?? []}
              onDelete={() => setShowDeleteRole(true)}
              onSaved={syncMyPermissions}
            />
          ) : null}
        </div>
      </div>

      {showDeleteRole && selectedRole && (
        <Modal
          onClose={() => setShowDeleteRole(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Administration · Delete role</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete role?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              Delete <span className="font-mono font-semibold text-ink-900">{selectedRole.code}</span>?
              Users assigned this role will lose its permissions immediately. This cannot be undone.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setShowDeleteRole(false)}>Cancel</button>
            <button
              className="btn-primary !bg-danger hover:!bg-red-700"
              disabled={deleteMut.isPending}
              onClick={() => { deleteMut.mutate(selectedRole.id); setShowDeleteRole(false); }}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete role'}
            </button>
          </div>
        </Modal>
      )}

      {showCreate && (
        <CreateRoleModal
          allPerms={allPermsQuery.data ?? []}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['roles'] });
            syncMyPermissions();
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// RoleDetail
// ============================================================
function RoleDetail({
  role, rolePermsQuery, allPerms, onDelete, onSaved,
}: {
  role: any;
  rolePermsQuery: any;
  allPerms: any[];
  onDelete: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const locked = LOCKED_CODES.includes(role.code);
  const permsLocked = role.is_system;

  const [editingMeta, setEditingMeta] = useState(false);
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');

  useEffect(() => {
    setName(role.name);
    setDescription(role.description ?? '');
    setEditingMeta(false);
  }, [role.id, role.name, role.description]);

  const patchMut = useMutation({
    mutationFn: (payload: any) => adminApi.updateRole(role.id, payload),
    onSuccess: () => {
      toast.success('Role updated');
      qc.invalidateQueries({ queryKey: ['roles'] });
      setEditingMeta(false);
      onSaved();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{role.code}</div>
          {editingMeta ? (
            <div className="space-y-2 mt-1">
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Role name"
              />
              <textarea
                className="input"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optional)"
              />
            </div>
          ) : (
            <>
              <div className="font-display font-bold text-lg text-ink-900">{role.name}</div>
              {role.description && (
                <div className="text-sm text-ink-600 mt-1">{role.description}</div>
              )}
            </>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {editingMeta ? (
            <>
              <button className="btn-secondary btn-sm" onClick={() => setEditingMeta(false)}>Cancel</button>
              <button
                className="btn-primary btn-sm"
                disabled={patchMut.isPending || name.trim().length < 2}
                onClick={() => patchMut.mutate({ name, description: description || null })}
              >
                Save
              </button>
            </>
          ) : (
            <>
              {!locked && (
                <button className="btn-secondary btn-sm" onClick={() => setEditingMeta(true)}>
                  Edit
                </button>
              )}
              {!role.is_system && (
                <button
                  className="text-xs font-semibold text-danger hover:bg-red-50 px-3 py-1.5 rounded-pill transition-colors"
                  onClick={onDelete}
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Protection notices */}
      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900 mb-4">
          <div className="font-semibold">Protected role</div>
          <div className="mt-0.5">This role cannot be renamed, deleted, or have its permissions changed.</div>
        </div>
      )}
      {role.is_system && !locked && (
        <div className="bg-canvas-subtle border border-ink-100 rounded-xl px-3 py-2 text-xs text-ink-700 mb-4">
          <div className="font-semibold">System role</div>
          <div className="mt-0.5">You can edit the display name and description, but the permission set is fixed.</div>
        </div>
      )}

      <PermissionsEditor
        roleId={role.id}
        readOnly={permsLocked}
        assigned={rolePermsQuery.data ?? []}
        allPerms={allPerms}
        isLoading={rolePermsQuery.isLoading}
        onSaved={onSaved}
      />
    </div>
  );
}

// ============================================================
// PermissionsEditor — grouped by module, full-replace PUT on save
// ============================================================
function PermissionsEditor({
  roleId, readOnly, assigned, allPerms, isLoading, onSaved,
}: {
  roleId: string;
  readOnly: boolean;
  assigned: any[];
  allPerms: any[];
  isLoading: boolean;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSelected(new Set(assigned.map((p) => p.code)));
  }, [roleId, assigned]);

  const saveMut = useMutation({
    mutationFn: () =>
      adminApi.updateRolePermissions(roleId, { permissionCodes: Array.from(selected) }),
    onSuccess: () => {
      toast.success('Permissions saved');
      qc.invalidateQueries({ queryKey: ['role-perms', roleId] });
      qc.invalidateQueries({ queryKey: ['roles'] });
      onSaved();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    const q = search.toLowerCase();
    for (const p of allPerms) {
      if (q && !p.code.toLowerCase().includes(q)) continue;
      (g[p.module] ??= []).push(p);
    }
    return g;
  }, [allPerms, search]);

  const toggle = (code: string) => {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleModule = (modulePerms: any[]) => {
    if (readOnly) return;
    const codes = modulePerms.map((p) => p.code);
    const allSelected = codes.every((c) => selected.has(c));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) codes.forEach((c) => next.delete(c));
      else codes.forEach((c) => next.add(c));
      return next;
    });
  };

  const originalSet = useMemo(() => new Set(assigned.map((p) => p.code)), [assigned]);
  const dirty = useMemo(() => {
    if (selected.size !== originalSet.size) return true;
    for (const c of selected) if (!originalSet.has(c)) return true;
    return false;
  }, [selected, originalSet]);

  if (isLoading) return <div className="text-ink-500 text-sm">Loading permissions…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="eyebrow text-ink-500">Permissions</div>
          <div className="text-sm text-ink-700 mt-0.5">
            <span className="font-semibold tabular-nums">{selected.size}</span>
            <span className="text-ink-400"> of </span>
            <span className="font-semibold tabular-nums">{allPerms.length}</span>
            <span className="text-ink-400"> granted</span>
          </div>
        </div>
        {!readOnly && (
          <button
            className="btn-primary btn-sm"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </button>
        )}
      </div>

      <div className="relative mb-3">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          className="input pl-9 text-sm"
          placeholder="Filter permissions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="border border-ink-100 rounded-xl max-h-[28rem] overflow-y-auto">
        {Object.keys(grouped).sort().map((modName) => {
          const perms = grouped[modName];
          const allSelected = perms.every((p) => selected.has(p.code));
          const someSelected = perms.some((p) => selected.has(p.code));
          const countSelected = perms.filter((p) => selected.has(p.code)).length;
          return (
            <div key={modName} className="border-b border-ink-100 last:border-0">
              <div className="bg-canvas-subtle px-3 py-2 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-2">
                  <div className="eyebrow text-ink-700">{modName}</div>
                  <span className="text-2xs text-ink-500 font-mono tabular-nums">
                    {countSelected}/{perms.length}
                  </span>
                </div>
                {!readOnly && (
                  <button
                    className="text-xs font-medium text-ink-600 hover:text-ink-900 transition-colors"
                    onClick={() => toggleModule(perms)}
                  >
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
              <div>
                {perms.map((p) => (
                  <label
                    key={p.code}
                    className={clsx(
                      'flex items-center px-3 py-1.5 text-xs hover:bg-canvas-subtle/50 transition-colors',
                      readOnly ? 'cursor-default' : 'cursor-pointer'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(p.code)}
                      disabled={readOnly}
                      onChange={() => toggle(p.code)}
                      className="mr-2.5 accent-sage-500"
                    />
                    <span className="font-mono flex-1 text-ink-800">{p.code}</span>
                    <span className="text-ink-400 font-mono text-2xs ml-2">{p.action}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
        {Object.keys(grouped).length === 0 && (
          <div className="text-center text-ink-500 p-6 text-sm">No permissions match.</div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// CreateRoleModal
// ============================================================
function CreateRoleModal({
  allPerms, onClose, onCreated,
}: { allPerms: any[]; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ code: '', name: '', description: '' });
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const createMut = useMutation({
    mutationFn: () => adminApi.createRole({
      code: form.code,
      name: form.name,
      description: form.description || null,
      permissionCodes: Array.from(selectedPerms),
    }),
    onSuccess: () => {
      toast.success('Role created');
      onCreated();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = /^[A-Z][A-Z0-9_]*$/.test(form.code) && form.name.trim().length >= 2;

  const toggle = (code: string) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const filteredPerms = useMemo(() => {
    if (!search) return allPerms;
    const lower = search.toLowerCase();
    return allPerms.filter((p) => p.code.toLowerCase().includes(lower));
  }, [allPerms, search]);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="eyebrow mb-1">Administration</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New role</h2>
        <p className="text-sm text-ink-500 mt-1">
          Custom roles let you assemble a specific permission set for your team.
        </p>
      </div>

      <div className="px-6 pb-4 flex-shrink-0 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Code</label>
            <input
              className="input font-mono"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="REGIONAL_MANAGER"
            />
            <div className="text-2xs text-ink-500 mt-1.5">UPPER_SNAKE_CASE, unique.</div>
          </div>
          <div>
            <label className="label">Display name</label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Regional Manager"
            />
          </div>
        </div>
        <div>
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea
            className="input"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Manages stores within an assigned region. Cannot edit company-wide settings."
          />
        </div>
      </div>

      <div className="px-6 flex-1 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow text-ink-500">Initial permissions</div>
          <div className="text-xs text-ink-500 font-mono tabular-nums">
            {selectedPerms.size} / {allPerms.length}
          </div>
        </div>
        <div className="relative mb-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="input pl-9 text-sm"
            placeholder="Filter permissions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="border border-ink-100 rounded-xl overflow-y-auto flex-1">
          {filteredPerms.map((p) => (
            <label
              key={p.code}
              className="flex items-center px-3 py-1.5 text-xs hover:bg-canvas-subtle/50 transition-colors border-b border-ink-100 last:border-0 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedPerms.has(p.code)}
                onChange={() => toggle(p.code)}
                className="mr-2.5 accent-sage-500"
              />
              <span className="font-mono flex-1 text-ink-800">{p.code}</span>
              <span className="text-ink-400 font-mono text-2xs ml-2">{p.module}</span>
            </label>
          ))}
          {filteredPerms.length === 0 && (
            <div className="text-center text-ink-500 p-6 text-sm">No permissions match.</div>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex-shrink-0 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!canSubmit || createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          {createMut.isPending ? 'Creating…' : 'Create role'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function PlusIcon() { return <svg {...iconProps}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
